import React, { useEffect, useState } from "react";
import { getDashboardMetrics } from "../services/metricsService";
import { Link } from "react-router-dom";

function MenuCard({ to, title, desc, badge, icon }) {
  return (
    <Link to={to} className="text-decoration-none">
      <div className="card shadow-sm h-100 border-0 menu-card">
        <div className="card-body d-flex gap-3 align-items-start">
          <div
            className="d-flex align-items-center justify-content-center rounded-3"
            style={{
              width: 44,
              height: 44,
              background: "rgba(13,110,253,.10)",
              flex: "0 0 auto",
              fontSize: 18,
            }}
          >
            {icon}
          </div>

          <div className="flex-grow-1">
            <div className="d-flex justify-content-between align-items-start gap-2">
              <div className="fw-semibold text-dark">{title}</div>
              {badge ? <span className="badge text-bg-primary">{badge}</span> : null}
            </div>
            <div className="text-muted small mt-1">{desc}</div>
          </div>
        </div>

        <div className="card-footer bg-transparent border-0 pt-0">
          <div className="small text-primary fw-semibold">Ouvrir →</div>
        </div>
      </div>
    </Link>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await getDashboardMetrics();
        setData(res);
      } catch (e) {
        console.error(e);
        setError(e?.message || "Erreur de chargement");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="d-flex vh-100 align-items-center justify-content-center">
        <div className="spinner-border" role="status" />
      </div>
    );
  }
  if (error) return <div className="container py-4 text-danger">{error}</div>;

  const c = data?.counts || {};
  const byDay = data?.byDay || [];
  const recent = data?.recentProducts || [];

  return (
    <div className="container py-4">
      {/* Styles hover cards */}
      <style>{`
        .menu-card { transition: transform .15s ease, box-shadow .15s ease; }
        .menu-card:hover { transform: translateY(-2px); box-shadow: 0 12px 24px rgba(0,0,0,.10) !important; }
      `}</style>

      {/* Header */}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h4 className="m-0">Dashboard</h4>
          <div className="text-muted small">Vue rapide + accès rapide aux modules</div>
        </div>
      </div>

      {/* Menus en cards */}
      <div className="row g-3 mb-3">
        <div className="col-12 col-md-6 col-lg-4">
          <MenuCard
            to="/admin/products"
            title="Produits"
            desc="Ajouter, modifier, gérer images/variantes et statut."
            badge={`${c.totalProducts ?? 0}`}
            icon="🛍️"
          />
        </div>

        <div className="col-12 col-md-6 col-lg-4">
          <MenuCard
            to="/admin/brands"
            title="Marques"
            desc="Créer et organiser les marques."
            badge={`${c.totalBrands ?? 0}`}
            icon="🏷️"
          />
        </div>

        <div className="col-12 col-md-6 col-lg-4">
          <MenuCard
            to="/admin/categories"
            title="Catégories"
            desc="Gérer catégories / sous-catégories."
            badge={`${c.totalCategories ?? 0}`}
            icon="🗂️"
          />
        </div>

        <div className="col-12 col-md-6 col-lg-4">
          <MenuCard
            to="/admin/stock"
            title="Stock"
            desc="Aperçu du stock, alertes et fiches de stock."
            badge={`Bas ≤3 : ${c.lowStockProducts ?? 0}`}
            icon="📦"
          />
        </div>

        <div className="col-12 col-md-6 col-lg-4">
          <MenuCard
            to="/admin/receptions/new"
            title="Réceptions"
            desc="Enregistrer les arrivages et entrées en stock."
            icon="✅"
          />
        </div>

        <div className="col-12 col-md-6 col-lg-4">
          <MenuCard
            to="/admin/orders"
            title="Commandes"
            desc="Suivre les commandes et statuts de paiement."
            icon="🧾"
          />
        </div>
      </div>

      {/* KPIs */}
      <div className="row g-3 mb-3">
        <div className="col-md-3">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <div className="text-muted small">Produits (total)</div>
              <div className="display-6">{c.totalProducts ?? 0}</div>
              <div className="d-flex flex-wrap gap-2 small mt-2">
                <span className="badge text-bg-success">actifs {c.activeProducts ?? 0}</span>
                <span className="badge text-bg-secondary">brouillons {c.draftProducts ?? 0}</span>
                <span className="badge text-bg-dark">archivés {c.archivedProducts ?? 0}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <div className="text-muted small">Marques</div>
              <div className="display-6">{c.totalBrands ?? 0}</div>
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <div className="text-muted small">Catégories</div>
              <div className="display-6">{c.totalCategories ?? 0}</div>
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <div className="text-muted small">Stock bas (≤ 3)</div>
              <div className="display-6">{c.lowStockProducts ?? 0}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Créations sur 7 jours */}
      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <h6 className="mb-3">Produits ajoutés (7 derniers jours)</h6>
          <div className="row g-2">
            {byDay.map((d, i) => (
              <div className="col-12 col-md-6" key={i}>
                <div className="d-flex align-items-center gap-3">
                  <div style={{ width: 110 }} className="text-muted small">
                    {d.key}
                  </div>
                  <div className="progress w-100" role="progressbar" aria-valuenow={d.count} aria-valuemin="0" aria-valuemax="20">
                    <div className="progress-bar" style={{ width: `${Math.min(100, d.count * 10)}%` }}>
                      {d.count}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {byDay.length === 0 && <div className="text-muted small ps-2">Aucune donnée récente.</div>}
          </div>
        </div>
      </div>

      {/* Derniers produits */}
      <div className="card shadow-sm">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="m-0">Derniers produits ajoutés</h6>
            <Link to="/admin/products" className="btn btn-sm btn-outline-primary">
              Voir tout
            </Link>
          </div>

          <div className="table-responsive">
            <table className="table align-middle table-hover">
              <thead>
                <tr>
                  <th>Produit</th>
                  <th>Statut</th>
                  <th>Prix</th>
                  <th>Créé le</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        {p.images?.[0] && (
                          <img
                            src={p.images[0]}
                            alt="img"
                            style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 6 }}
                          />
                        )}
                        <div>
                          <div className="fw-semibold">{p.title}</div>
                          <div className="text-muted small">{p.brandId || "—"}</div>
                        </div>
                      </div>
                    </td>

                    <td>
                      <span
                        className={`badge text-bg-${
                          p.status === "active" ? "success" : p.status === "draft" ? "secondary" : "dark"
                        }`}
                      >
                        {p.status}
                      </span>
                    </td>

                    <td>{typeof p.price === "number" ? (p.price / 100).toFixed(2) : "—"}</td>

                    <td className="text-muted small">
                      {p.createdAt?.toDate ? p.createdAt.toDate().toLocaleString() : "—"}
                    </td>
                  </tr>
                ))}

                {recent.length === 0 && (
                  <tr>
                    <td colSpan={4} className="text-muted small">
                      Aucun produit pour le moment.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
